/**
 * Form field.
 *
 * The label is a real `<label>` bound by `htmlFor`, and the error is bound by `aria-describedby`
 * with `role="alert"`. Both matter: a placeholder is not a label, and an error rendered as plain
 * red text next to an input is invisible to a screen reader, which is precisely the user least able
 * to guess what went wrong.
 */

import { useId, type InputHTMLAttributes } from 'react';

import './primitives.css';

export interface FieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'id' | 'className'
> {
  label: string;
  /** Field-level message from the API envelope's `fields`, or from local validation. */
  error?: string | undefined;
  hint?: string | undefined;
}

export function Field({ label, error, hint, ...rest }: FieldProps): React.JSX.Element {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>

      <input
        id={id}
        className={`field__input${error ? ' field__input--error' : ''}`}
        // Announces the invalid state itself, rather than relying on the border colour — which a
        // colour-blind or screen-reader user does not receive.
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        {...rest}
      />

      {hint ? (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      ) : null}

      {error ? (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
